import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Sparkles, Music } from "lucide-react";
import { EVENTS, EVENT_EXTRAS, waLink } from "@/data/site";
import CTASection from "@/components/CTASection";
import ComboSection from "@/components/ComboSection";
import PackagesGrid from "@/components/PackagesGrid";
import MasonryGallery from "@/components/MasonryGallery";
import { SERVICE } from "@/constants/testIds";

const Events = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative h-[85vh] overflow-hidden">
        <img
          src={EVENTS.hero}
          alt="Events"
          className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28 container-x">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="eyebrow text-gold mb-4">
            Van kleine feesten tot grote gala's
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }} className="font-serif text-white text-6xl md:text-7xl lg:text-8xl leading-[1.02] max-w-3xl">
            Events. <span className="italic text-gold">Compleet verzorgd.</span>
          </motion.h1>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-narrow">
          <p className="font-serif text-2xl md:text-3xl leading-snug max-w-3xl">{EVENTS.intro}</p>
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {EVENTS.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-sm text-neutral-700">
                <Check size={14} className="text-gold mt-1 shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Photography Packages */}
      <section className="bg-beige py-24 md:py-32">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow mb-3">Fotografiepakketten</p>
            <h2 className="font-serif text-4xl md:text-5xl">Kies jouw eventreportage</h2>
          </div>
          <PackagesGrid packages={EVENTS.packages} serviceName="Events" />

          {/* Extra options */}
          <div className="max-w-3xl mx-auto mt-16 bg-white p-8 md:p-10">
            <p className="eyebrow mb-4">Extra opties</p>
            <ul className="divide-y divide-neutral-200">
              {EVENTS.extraOpties.map((e) => (
                <li key={e.name} className="flex justify-between py-3 text-sm">
                  <span className="text-neutral-800">{e.name}</span>
                  <span className="font-serif text-gold text-lg">{e.price}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Extra beleving — Magic Mirror & Audio */}
      <section className="bg-ink text-white py-24 md:py-32">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow mb-4">Extra beleving</p>
            <h2 className="font-serif text-4xl md:text-5xl">
              Maak van jouw feest <span className="italic text-gold">een herinnering.</span>
            </h2>
            <p className="mt-4 text-white/60 text-sm max-w-lg mx-auto">
              Combineer fotografie met de Magic Mirror en het Audio Gastenboek — de perfecte mix van fun, beleving en emotie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Link to="/magic-mirror" className="group relative overflow-hidden bg-white/5 border border-white/10 hover:border-gold transition-colors p-10">
              <Sparkles size={26} className="text-gold mb-6" strokeWidth={1.4} />
              <h3 className="font-serif text-3xl mb-3">Magic Mirror Fotobooth</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Interactieve fotospiegel met luxe animaties, custom design en onmiddellijke prints. De highlight op elk feest.
              </p>
              <p className="mt-6 font-serif text-2xl text-gold">vanaf €395</p>
              <span className="mt-6 inline-block text-[10px] uppercase tracking-[0.3em] text-gold group-hover:underline">
                Ontdek →
              </span>
            </Link>
            <Link to="/audio-gastenboek" className="group relative overflow-hidden bg-white/5 border border-white/10 hover:border-gold transition-colors p-10">
              <Music size={26} className="text-gold mb-6" strokeWidth={1.4} />
              <h3 className="font-serif text-3xl mb-3">Audio Gastenboek</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Een vintage telefoon waar gasten een persoonlijke boodschap kunnen achterlaten. De stemmen van de mensen die er waren — voor altijd bewaard.
              </p>
              <p className="mt-6 font-serif text-2xl text-gold">vanaf €195</p>
              <span className="mt-6 inline-block text-[10px] uppercase tracking-[0.3em] text-gold group-hover:underline">
                Ontdek →
              </span>
            </Link>
          </div>

          {/* All extras */}
          <div className="max-w-4xl mx-auto mt-16">
            <p className="eyebrow text-center mb-8">Aanvullende opties</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
              {EVENT_EXTRAS.map((e) => (
                <li key={e.name} className="flex justify-between border-b border-white/10 py-3 text-sm">
                  <span className="text-white/85">{e.name}</span>
                  <span className="font-serif text-gold">{e.price}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center mt-14">
            <a
              data-testid={`${SERVICE.bookBtn}-events-info`}
              href={waLink("Hallo Eric, ik wil vrijblijvend info over een event met Magic Mirror en Audio Gastenboek.")}
              target="_blank"
              rel="noreferrer"
              className="btn-gold"
            >
              Vrijblijvend info aanvragen
            </a>
          </div>
        </div>
      </section>

      {/* Combos */}
      <ComboSection ids={EVENTS.combos} title="Populaire feestcombo's" />

      {/* Gallery */}
      <MasonryGallery filterable={false} initialTag="events" />

      <CTASection />
    </>
  );
};

export default Events;
